import { useMemo } from 'react'

import {
  formatDate, matchFilterArrayIncludes, matchFilterBetweenDates, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignQuoteModel, CampaignQuoteRequestOptions, CampaignQuoteStatusKeys } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

import { sortByPriorityCampaigns } from '@functions/priorityCampaign'

const watchEntityKeys = ['campaigns', 'campaignQuotes', 'statuses']

const approvedStatusKeys = ['approved', 'void']
const pendingStatusKeys = ['awaiting_review', 'pending']

type CampaignQuoteFilters = {
  agency_id?: number,
  agency_payment_method_id?: number,
  authorization_sent?: string,
  authorized?: string,
  campaign_id?: number,
  campaign_quote_template_id?: number,
  created_date_end?: string,
  created_date_start?: string,
  limit?: number,
  order_by?: string,
  priority_quotes_by_user_id?: number,
  sort_by_priority_campaign_for_user_id?: number,
  status_key?: CampaignQuoteStatusKeys | 'all',
  status?: 'approved' | 'pending',
  string?: string,
}

type UseCampaignQuotesOptions = {
  filters?: CampaignQuoteFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteRequestOptions,
  sortBy?: string,
}

function useCampaignQuotes(options: UseCampaignQuotesOptions = {}) {
  const { filters = {}, requestOptions, sortBy = 'title asc' } = options

  const {
    agency_id: filterAgencyId,
    agency_payment_method_id: filterAgencyPaymentMethodId,
    authorization_sent: filterAuthorizationSent,
    authorized: filterAuthorized,
    campaign_id: filterCampaignId,
    campaign_quote_template_id: filterCampaignQuoteTemplateId,
    created_date_end: filterCreatedDateEnd,
    created_date_start: filterCreatedDateStart,
    limit: filterLimit,
    sort_by_priority_campaign_for_user_id: sortByPriorityCampaignUserId,
    priority_quotes_by_user_id: filterPriorityQuotesByUserId,
    status: filterStatus,
    status_key: filterStatusKey,
    string: filterString,
  } = filters

  const {
    updatedEntities: {
      campaignQuotes: campaignQuotesUpdatedAt,
      campaigns: campaignsUpdatedAt,
      statuses: statusesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignQuotes, statuses } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignQuotes = useMemo(() => {
    const filtered = Object.values(campaignQuotes).filter((campaignQuote: CampaignQuoteModel) => {
      const {
        agency_payment_method_id,
        campaign_id,
        campaign_quote_template_id,
        id,
        status_id,
        authorized,
        agency_id,
        authorization_sent,
        dates,
        priority_campaign_user_ids = [],
        title,
      } = campaignQuote

      const createdAt = formatDate(dates?.created?.date_time, 'dd/MM/yyyy HH:mm')

      const matchAgency = matchFilterNumber(agency_id, filterAgencyId)
      const matchAgencyPaymentMethod = matchFilterNumber(agency_payment_method_id, filterAgencyPaymentMethodId)
      const matchAuthorization = filterAuthorized === 'all'
      || matchFilterString(authorized?.toString(), filterAuthorized)
      const matchAuthorizationSent = filterAuthorizationSent === 'all'
      || matchFilterString(authorization_sent?.toString(), filterAuthorizationSent)
      const matchCampaignId = matchFilterNumber(campaign_id, filterCampaignId)
      const matchCampaignQuoteTemplateId = matchFilterNumber(campaign_quote_template_id, filterCampaignQuoteTemplateId)

      // Match dates
      const matchCreatedAt = matchFilterBetweenDates(createdAt, filterCreatedDateStart, filterCreatedDateEnd)

      // Match String by Title or ID
      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const status = status_id && statuses[status_id] ? statuses[status_id] : {}

      // Match Status
      const matchStatusKey = filterStatusKey === 'all'
        // @ts-expect-error TS2339
        ? true : matchFilterString(status?.key, filterStatusKey, { exact: true })

      const matchStatus = filterStatus
        // @ts-expect-error TS2339
        ? (filterStatus === 'pending' && pendingStatusKeys.includes(status.key))
        // @ts-expect-error TS2339
        || (filterStatus === 'approved' && approvedStatusKeys.includes(status.key))
        : true

      const matchPriorityQuotes = filterPriorityQuotesByUserId
        ? matchFilterArrayIncludes(priority_campaign_user_ids, Number(filterPriorityQuotesByUserId)) : true

      return matchAgency
      && matchAgencyPaymentMethod
      && matchAuthorization
      && matchAuthorizationSent
      && matchCampaignId
      && matchCampaignQuoteTemplateId
      && matchCreatedAt
      && matchPriorityQuotes
      && matchStatus
      && matchStatusKey
      && matchString
    })

    const [column, direction] = sortBy.split(' ')
    return sortByPriorityCampaignUserId
      ? sortByPriorityCampaigns({ entities: filtered, selectedPriorityUserId: sortByPriorityCampaignUserId })
      // @ts-expect-error TS2345
      : sortArrayBy(filtered, direction, column)
  }, [campaignQuotesUpdatedAt, campaignsUpdatedAt, statusesUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignQuotesCount = filteredCampaignQuotes.length
  const hasFilteredCampaignQuotes = !!filteredCampaignQuotesCount

  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredCampaignQuotesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignQuotes } = useReduxAction(
    'campaignQuotes',
    'loadCampaignQuotes',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignQuotes,
    filteredCampaignQuotesCount,
    hasFilteredCampaignQuotes,
    loading: loadingCampaignQuotes,
  }
}

export default useCampaignQuotes
